import React, { useState } from "react";

import { useMediaQuery } from "react-responsive";
import { useHistory } from "react-router-dom";

import { PropertyIntegrationMark } from "manager/components/IntegrationMark";
import ROUTES from "manager/config/routes";
import { Property } from "manager/interfaces/api";
import { getIntegrationType } from "manager/utils/integration.util";
import { Button } from "shared/components/Button";
import { Checkbox } from "shared/components/Form";
import Icon from "shared/components/Icon";
import Table from "shared/components/Table";
import Tooltip from "shared/components/Tooltip";
import {
  BREAKPOINT_RESOLUTIONS,
  BREAKPOINT_TYPES,
  INTEGRATION_TYPES,
} from "shared/config/constants";
import withBreakpoint from "shared/hocs/withBreakpoint";
import { transformRoute } from "shared/utils/routing";

import {
  AssignEmployeesToPropertyParams,
  AssignPropertyToPortfolioParams,
  CreateUnitParams,
  DefaultPagination,
  PropertiesSearchParams,
  UnassignPropertyFromPortfolioParams,
} from "../interfaces";
import {
  GreyLabel,
  PortfolioLabel,
  PropertyIntegrationMarkContainer,
} from "../PropertiesTable/styled";

import {
  BILLING_COLUMN_WIDTH,
  BILLING_STATUS,
  billableUnitsLabel,
  canCorrectGoLive,
  canSetLive,
  comparePropertyName,
  comparePropertyPortfolio,
  formatBillingDate,
  onboardingBulkSetLiveLabel,
  propertyOnLabel,
  turnedOffOnLabel,
  selectedPropertyIds,
  SCHEDULED_CHIP_SUB,
  STOPPING_CHIP_SUB,
} from "./billingDisplay";
import {
  BillingChip,
  BillingTableScroll,
  BulkSetLiveBar,
  BulkSetLiveHint,
  ChipSub,
  NameCell,
} from "./styled";

interface BillingSectionTableProps {
  properties?: Property[];
  propertiesCount: number;
  loading: boolean;
  hasInputValue: boolean;
  pagination: DefaultPagination;
  setPagination: (next: DefaultPagination) => void;
  propertiesSearchParams: PropertiesSearchParams;
  section: "onboarding" | "live" | "notLive";
  isMagicPortalEnabled: boolean;
  showPrequalificationCheckbox: boolean;
  onTogglePropertyDisplayOnPortal: (propertyId: string | number) => void;
  onTogglePropertyPrequalification: (propertyId: string | number) => void;
  createUnit: (context: CreateUnitParams) => void;
  assignEmployeesToProperty: (context: AssignEmployeesToPropertyParams) => void;
  unassignPropertyFromPortfolio: (
    context: UnassignPropertyFromPortfolioParams
  ) => void;
  assignPropertyToPortfolio: (context: AssignPropertyToPortfolioParams) => void;
  onSetLive: (properties: Property[]) => void;
  onStop: (property: Property) => void;
  onCorrect: (property: Property) => void;
  onCancelScheduled: (property: Property) => void;
  onEditUnits: (property: Property) => void;
  onHistory: (property: Property) => void;
}

interface BillingSectionSelectionProps {
  section: BillingSectionTableProps["section"];
  properties: Property[];
  onSetLive: (properties: Property[]) => void;
  hasInputValue: boolean;
  loading: boolean;
  rows: any[];
  columns: any[];
  page: number;
  pageSize: number;
  propertiesCount: number;
  setPagination: (next: DefaultPagination) => void;
  onRowClick: (key: string | number) => void;
  breakpoint?: string;
}

const BillingSectionSelectionView = ({
  section,
  properties,
  onSetLive,
  hasInputValue,
  loading,
  rows,
  columns,
  page,
  pageSize,
  propertiesCount,
  setPagination,
  onRowClick,
  breakpoint,
}: BillingSectionSelectionProps) => {
  // Same token Shared Table uses to mount MobileTable / MobileActionBar.
  const isXs = breakpoint === BREAKPOINT_TYPES.xs;
  const [selectedKeys, setSelectedKeys] = useState<Array<string | number>>([]);
  const selected = selectedPropertyIds(selectedKeys, properties);
  const onboardingSetLiveAction = {
    key: "SET_LIVE",
    label: "Set live",
    primary: true,
    onSelect: (keys: Array<string | number>) => {
      const next = selectedPropertyIds(keys, properties);
      if (next.length) {
        onSetLive(next);
      }
    },
  };
  // xs: MobileActionBar is the bulk control (empty [] draws a blank footer).
  // Desktop: [] keeps checkboxes without the header ▾; the bar is the control.
  const checkboxActions =
    section === "onboarding"
      ? isXs
        ? [onboardingSetLiveAction]
        : []
      : undefined;

  return (
    <>
      {section === "onboarding" && !isXs && selected.length > 0 && (
        <BulkSetLiveBar>
          <Button type="primary" size="sm" onClick={() => onSetLive(selected)}>
            {onboardingBulkSetLiveLabel(selected.length)}
          </Button>
          <BulkSetLiveHint>
            {selected.length === 1
              ? "1 property selected"
              : `${selected.length} properties selected`}
          </BulkSetLiveHint>
        </BulkSetLiveBar>
      )}
      <BillingTableScroll>
        <Table
          searching={hasInputValue}
          dropdownItems
          alt
          loading={loading}
          columns={columns}
          rows={rows}
          onRowSelection={setSelectedKeys}
          columnBreakpoints={{
            xs: { column1: "name", column2: "action" },
            sm: [
              "name",
              "numUnits",
              "billableUnits",
              "displayOnPortal",
              "prequalification",
              "action",
            ],
            md: [
              "name",
              "numUnits",
              "billableUnits",
              "displayOnPortal",
              "prequalification",
              "action",
            ],
            lg: [
              "name",
              "portfolio",
              "displayOnPortal",
              "prequalification",
              "numUnits",
              "billableUnits",
              "propertyOn",
              "liveSince",
              "previouslyLive",
              "stoppedOn",
              "action",
            ],
            xl: [
              "name",
              "address",
              "portfolio",
              "displayOnPortal",
              "prequalification",
              "numUnits",
              "billableUnits",
              "propertyOn",
              "liveSince",
              "previouslyLive",
              "stoppedOn",
              "action",
            ],
          }}
          onRowClick={onRowClick}
          placeholder={{ label: "Nothing here", Icon: Icon.NoPropertiesIcon }}
          checkboxActions={checkboxActions}
          pagination={{
            current: page,
            onChange: (currentPage) =>
              setPagination({ page: currentPage, pageSize }),
            total: propertiesCount,
            defaultPageSize: pageSize,
          }}
        />
      </BillingTableScroll>
    </>
  );
};

const BillingSectionSelection = withBreakpoint(BillingSectionSelectionView);

const BillingSectionTable = ({
  properties = [],
  propertiesCount,
  loading,
  hasInputValue,
  pagination,
  setPagination,
  propertiesSearchParams,
  section,
  isMagicPortalEnabled,
  showPrequalificationCheckbox,
  onTogglePropertyDisplayOnPortal,
  onTogglePropertyPrequalification,
  createUnit,
  assignEmployeesToProperty,
  unassignPropertyFromPortfolio,
  assignPropertyToPortfolio,
  onSetLive,
  onStop,
  onCorrect,
  onCancelScheduled,
  onEditUnits,
  onHistory,
}: BillingSectionTableProps) => {
  const history = useHistory();
  const isXxlScreen = useMediaQuery({ minWidth: BREAKPOINT_RESOLUTIONS.xxl });
  const isXxxlScreen = useMediaQuery({ minWidth: BREAKPOINT_RESOLUTIONS.xxxl });
  const { page, pageSize } = pagination;
  const rowIds = properties.map((property) => property.id).join(",");

  const getNameWithTooltip = (property, dataTestId) => {
    const { name } = property;
    const integrationEntrata =
      getIntegrationType(property) === INTEGRATION_TYPES.ENTRATA;
    const tooltipLength = isXxxlScreen ? 50 : isXxlScreen ? 30 : 20;
    const isLongName = name.length > tooltipLength;
    return (
      <NameCell key={`${property.id}-name`} data-testid={dataTestId}>
        <Tooltip
          theme="light"
          placement="topLeft"
          title={isLongName ? name : ""}
        >
          {`${name.substring(0, tooltipLength)}${isLongName ? "..." : ""}`}
        </Tooltip>
        <PropertyIntegrationMarkContainer
          opacity={integrationEntrata ? 0.75 : 1}
        >
          <PropertyIntegrationMark property={property} />
        </PropertyIntegrationMarkContainer>
      </NameCell>
    );
  };

  const rows = properties.map((property, index) => {
    const fullAddress = `${property.address}, ${property?.city}, ${property?.state} ${property?.zipcode}`;
    const turnedOffOn = turnedOffOnLabel(property);
    const dropdownItems = [
      {
        key: "NEW_UNIT",
        label: "+ New Unit",
        onClick: () =>
          createUnit({
            propertyName: property.name,
            propertyId: property.id,
            propertiesSearchParams,
          }),
      },
      {
        key: "ASSIGN_EMPLOYEES",
        label: "Assign Employees",
        onClick: () =>
          assignEmployeesToProperty({
            propertyId: property.id,
            propertyName: property.name,
          }),
      },
      property.portfolioIsDefault
        ? {
            key: "ASSIGN_TO_PORTFOLIO",
            label: "Assign to portfolio",
            onClick: () =>
              assignPropertyToPortfolio({
                propertyId: property.id,
                propertyName: property.name,
                portfolioId: property.portfolio,
              }),
          }
        : {
            key: "UNASSIGN_FROM_PORTFOLIO",
            label: "Unassign From Portfolio",
            onClick: () =>
              unassignPropertyFromPortfolio({
                portfolioId: property.portfolio,
                portfolioName: property.portfolioName,
                property: { id: property.id, name: property.name },
              }),
          },
      ...(property.billingStatus === BILLING_STATUS.scheduled
        ? [
            {
              key: "CANCEL_SCHEDULED",
              label: "Cancel scheduled go-live",
              onClick: () => onCancelScheduled(property),
            },
          ]
        : []),
      ...(canCorrectGoLive(property)
        ? [
            {
              key: "CORRECT_GO_LIVE",
              label: "Correct go-live date",
              onClick: () => onCorrect(property),
            },
          ]
        : []),
      ...(property.billingStatus === BILLING_STATUS.live ||
      property.billingStatus === BILLING_STATUS.stopping
        ? [
            {
              key: "EDIT_UNITS",
              label: "Edit billable units",
              onClick: () => onEditUnits(property),
            },
          ]
        : []),
      ...(property.billingStatus === BILLING_STATUS.live
        ? [
            {
              key: "STOP_BILLING",
              label: "Stop billing",
              onClick: () => onStop(property),
            },
          ]
        : []),
      ...(property.billingStatus === BILLING_STATUS.stopping
        ? [
            {
              key: "CANCEL_SCHEDULED",
              label: "Cancel scheduled stop",
              onClick: () => onCancelScheduled(property),
            },
          ]
        : []),
      {
        key: "HISTORY",
        label: "History",
        onClick: () => onHistory(property),
      },
    ];

    return {
      key: property.id,
      rawData: property,
      values: [
        getNameWithTooltip(property, `property-${index}-name`),
        <GreyLabel
          key={`${property.id}-address`}
          data-testid={`property-${index}-address`}
        >
          {fullAddress}
        </GreyLabel>,
        property.portfolioIsDefault ? (
          <GreyLabel key={`${property.id}-portfolio`}>No portfolio</GreyLabel>
        ) : (
          <PortfolioLabel key={`${property.id}-portfolio`}>
            {property.portfolioName}
            {property.dealSetupId && (
              <Tooltip
                theme="light"
                title="This property has settings different from current portfolio settings"
              >
                <Icon.InfoIcon />
              </Tooltip>
            )}
          </PortfolioLabel>
        ),
        ...(isMagicPortalEnabled
          ? [
              <Checkbox
                key={`${property.id}-display-on-portal`}
                id={`displayOnPortal-${property.id}`}
                onChange={() => onTogglePropertyDisplayOnPortal(property.id)}
                value={property.displayOnPortal}
              />,
            ]
          : []),
        ...(showPrequalificationCheckbox
          ? [
              <Checkbox
                key={`${property.id}-prequalification`}
                id={`prequalification-${property.id}`}
                onChange={() => onTogglePropertyPrequalification(property.id)}
                value={property.eligibleForPrescreening}
              />,
            ]
          : []),
        <span
          key={`${property.id}-units`}
          data-testid={`property-${index}-numUnits`}
        >
          {`${property.numUnitsRented} / ${property.numUnits}`}
        </span>,
        <span
          key={`${property.id}-billable-units`}
          data-testid={`property-${index}-billableUnits`}
        >
          {billableUnitsLabel(property)}
        </span>,
        <span
          key={`${property.id}-property-on`}
          data-testid={`property-${index}-propertyOn`}
        >
          <div>{propertyOnLabel(property)}</div>
          {turnedOffOn ? (
            <BillingChip>
              <strong>{turnedOffOn}</strong>
            </BillingChip>
          ) : null}
        </span>,
        ...(section === "live"
          ? [
              <span key={`${property.id}-live-since`}>
                <div>{formatBillingDate(property.billingLiveAt)}</div>
                {property.billingStatus === BILLING_STATUS.stopping && (
                  <BillingChip tone="stopping">
                    <strong>
                      Stopping {formatBillingDate(property.billingStoppedAt)}
                    </strong>
                    <ChipSub>{STOPPING_CHIP_SUB}</ChipSub>
                  </BillingChip>
                )}
              </span>,
            ]
          : []),
        ...(section === "notLive"
          ? [
              <span key={`${property.id}-previously-live`}>
                {formatBillingDate(property.billingLiveAt)}
              </span>,
              <span key={`${property.id}-stopped-on`}>
                {formatBillingDate(property.billingStoppedAt)}
              </span>,
            ]
          : []),
        section === "onboarding" &&
        property.billingStatus === BILLING_STATUS.scheduled ? (
          <BillingChip key={`${property.id}-action`} tone="scheduled">
            <strong>
              Goes live {formatBillingDate(property.billingLiveAt)}
            </strong>
            <ChipSub>{SCHEDULED_CHIP_SUB}</ChipSub>
          </BillingChip>
        ) : canSetLive(property) ? (
          <Button
            key={`${property.id}-action`}
            type="secondary"
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              onSetLive([property]);
            }}
          >
            {section === "notLive" ? "Go live" : "Set live"}
          </Button>
        ) : (
          <span key={`${property.id}-action`} />
        ),
      ],
      dropdownItems,
    };
  });

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: BILLING_COLUMN_WIDTH.name,
      sorter: comparePropertyName,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      width: BILLING_COLUMN_WIDTH.address,
    },
    {
      title: "Portfolio",
      dataIndex: "portfolio",
      key: "portfolio",
      width: BILLING_COLUMN_WIDTH.portfolio,
      sorter: comparePropertyPortfolio,
    },
    ...(isMagicPortalEnabled
      ? [
          {
            title: "Display on Portal",
            dataIndex: "displayOnPortal",
            key: "displayOnPortal",
            width: BILLING_COLUMN_WIDTH.displayOnPortal,
            onCell: () => ({
              className: "cursor-arrow",
              onClick: (event) => {
                event.preventDefault();
                event.stopPropagation();
              },
            }),
          },
        ]
      : []),
    ...(showPrequalificationCheckbox
      ? [
          {
            title: "Prequalification",
            dataIndex: "prequalification",
            key: "prequalification",
            width: BILLING_COLUMN_WIDTH.prequalification,
            onCell: () => ({
              className: "cursor-arrow",
              onClick: (event) => {
                event.preventDefault();
                event.stopPropagation();
              },
            }),
          },
        ]
      : []),
    {
      title: "Units: Rented / All",
      dataIndex: "numUnits",
      key: "numUnits",
      width: BILLING_COLUMN_WIDTH.numUnits,
    },
    {
      title: "Billable units",
      dataIndex: "billableUnits",
      key: "billableUnits",
      width: BILLING_COLUMN_WIDTH.billableUnits,
    },
    {
      title: "Property on",
      dataIndex: "propertyOn",
      key: "propertyOn",
      width: BILLING_COLUMN_WIDTH.propertyOn,
    },
    ...(section === "live"
      ? [
          {
            title: "Live on VERO Since",
            dataIndex: "liveSince",
            key: "liveSince",
            width: BILLING_COLUMN_WIDTH.liveSince,
          },
        ]
      : []),
    ...(section === "notLive"
      ? [
          {
            title: "Previously live",
            dataIndex: "previouslyLive",
            key: "previouslyLive",
            width: BILLING_COLUMN_WIDTH.previouslyLive,
          },
          {
            title: "Stopped on",
            dataIndex: "stoppedOn",
            key: "stoppedOn",
            width: BILLING_COLUMN_WIDTH.stoppedOn,
          },
        ]
      : []),
    {
      title: "",
      dataIndex: "action",
      key: "action",
      width: BILLING_COLUMN_WIDTH.action,
      className: "billing-action-cell",
    },
  ];

  return (
    <BillingSectionSelection
      key={`${page}-${rowIds}`}
      section={section}
      properties={properties}
      onSetLive={onSetLive}
      hasInputValue={hasInputValue}
      loading={loading}
      rows={rows}
      columns={columns}
      page={page}
      pageSize={pageSize}
      propertiesCount={propertiesCount}
      setPagination={setPagination}
      onRowClick={(key) =>
        history.push(transformRoute(ROUTES.property, { id: key }))
      }
    />
  );
};

export default BillingSectionTable;
